import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, ChevronDownIcon, SparklesIcon, MessageSquareIcon, TrendingDownIcon, CheckCheckIcon, UserIcon, BriefcaseIcon, SettingsIcon, LogOutIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { RegionSelector } from './RegionSelector';
type Notification = {
  id: string;
  icon: React.ElementType;
  title: string;
  detail: string;
  time: string;
  read: boolean;
};
const initialNotifications: Notification[] = [{
  id: '1',
  icon: SparklesIcon,
  title: '3 new matches found',
  detail: 'For "Prado under AED 90,000"',
  time: '5m ago',
  read: false
}, {
  id: '2',
  icon: MessageSquareIcon,
  title: 'Seller replied',
  detail: 'Al Quoz Motors sent you a message',
  time: '1h ago',
  read: false
}, {
  id: '3',
  icon: TrendingDownIcon,
  title: 'Price dropped',
  detail: 'A saved villa is now AED 120k lower',
  time: 'Yesterday',
  read: true
}];
export function TopHeader() {
  const {
    isAuthenticated,
    openLogin,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(initialNotifications);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const unread = notifications.filter(n => !n.read).length;
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };
  const markAllRead = () => setNotifications(prev => prev.map(n => ({
    ...n,
    read: true
  })));
  return <header className="hidden lg:flex items-center justify-end gap-4 px-8 py-5 absolute top-0 right-0 z-50 pointer-events-none [&>*]:pointer-events-auto" data-id="element-2994" data-mp="2fk4tsqcdogob">
      <RegionSelector data-id="element-2995" data-mp="2pazh7y1d1mbc" />
      {isAuthenticated ? <div className="flex items-center gap-4" data-id="element-2996" data-mp="2papo41l0qz1f">
          <div className="relative" ref={panelRef} data-id="element-2997" data-mp="2kbmkmlkot0vk">
            <button onClick={() => setOpen(o => !o)} aria-label="Notifications" className={`relative p-2 rounded-full transition-colors ${open ? 'text-primary-indigo bg-primary-indigo/5' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`} data-id="element-2998" data-mp="2kcxyxfbrdr0x">
              <BellIcon className="w-5 h-5" data-id="element-2999" data-mp="3cwjlr8mx73jg" />
              {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-error text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-id="element-3000" data-mp="3hx7cdt78lid5">
                  {unread}
                </span>}
            </button>

            <AnimatePresence data-id="element-3001" data-mp="2u8en2fl4e8ri">
              {open && <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} transition={{
            duration: 0.15
          }} className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50" data-id="element-3002" data-mp="2u6tu2uegexvr">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" data-id="element-3003" data-mp="23axdrfykw7jn">
                    <h3 className="font-display font-bold text-dark-navy" data-id="element-3004" data-mp="1yfi5d3jcb3um">
                      Notifications
                    </h3>
                    {unread > 0 && <button onClick={markAllRead} className="flex items-center gap-1 text-xs font-medium text-primary-indigo hover:underline" data-id="element-3005" data-mp="1pihk35rkhlve">
                        <CheckCheckIcon className="w-3.5 h-3.5" data-id="element-3006" data-mp="1fktm86ynzy90" />
                        Mark all read
                      </button>}
                  </div>

                  <div className="max-h-80 overflow-y-auto" data-id="element-3007" data-mp="39w8d4r3ew3qc">
                    {notifications.map(n => {
                const Icon = n.icon;
                return <div key={n.id} className={`flex gap-3 px-4 py-3 border-b border-slate-50 last:border-0 transition-colors hover:bg-slate-50 ${!n.read ? 'bg-primary-indigo/[0.03]' : ''}`} data-id="element-3008" data-mp="rn5gbqcr5tpm">
                          <div className="w-9 h-9 rounded-full bg-primary-indigo/10 flex items-center justify-center flex-shrink-0" data-id="element-3009" data-mp="15fblyiia4iat">
                            <Icon className="w-4 h-4 text-primary-indigo" data-id="element-3010" data-mp="8qng0kju8lhr" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-3011" data-mp="do5ok9ty0yp8">
                            <p className="text-sm font-medium text-dark-navy" data-id="element-3012" data-mp="hp7pqw7f7ktu">
                              {n.title}
                            </p>
                            <p className="text-xs text-slate-500 truncate" data-id="element-3013" data-mp="mm7nmju49h9v">
                              {n.detail}
                            </p>
                            <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-3014" data-mp="1y2pkum5jfnxk">
                              {n.time}
                            </p>
                          </div>
                          {!n.read && <span className="w-2 h-2 bg-primary-indigo rounded-full flex-shrink-0 mt-1.5" data-id="element-3015" data-mp="3q4quh9r1hy3q" />}
                        </div>;
              })}
                  </div>

                  <button className="w-full py-3 text-sm font-medium text-primary-indigo hover:bg-slate-50 transition-colors border-t border-slate-100" data-id="element-3016" data-mp="2yrm9dojq3yy">
                    View all
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="relative" ref={menuRef} data-id="element-3017" data-mp="3l80j2zq6jqon">
            <button onClick={() => setMenuOpen(o => !o)} aria-label="Account menu" aria-haspopup="menu" aria-expanded={menuOpen} className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" data-id="element-3018" data-mp="3gefozirpjguq">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="User avatar" className="w-10 h-10 rounded-full object-cover border border-slate-200" data-id="element-3019" data-mp="137zcif13ethl" />
              <ChevronDownIcon className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`} data-id="element-3020" data-mp="yapo92pfwnvc" />
            </button>

            <AnimatePresence data-id="element-3021" data-mp="1qyljs439w9qj">
              {menuOpen && <motion.div initial={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 8,
            scale: 0.98
          }} transition={{
            duration: 0.15
          }} role="menu" className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50" data-id="element-3022" data-mp="1qvzcm5kanw8u">
                  <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100" data-id="element-3023" data-mp="j9u8wnwh4f1g">
                    <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" data-id="element-3024" data-mp="1gvj7i9gbcesp" />
                    <div className="min-w-0" data-id="element-3025" data-mp="126vf2c1rkl6z">
                      <p className="text-sm font-bold text-dark-navy truncate" data-id="element-3026" data-mp="1zn29ccylpmt5">
                        Ahmed Al Mansoori
                      </p>
                      <p className="text-xs text-slate-500 truncate" data-id="element-3027" data-mp="1uvaq6nhryjd1">
                        ahmed@example.com
                      </p>
                    </div>
                  </div>

                  <div className="py-1" data-id="element-3028" data-mp="275s2qv10p76t">
                    <button role="menuitem" onClick={() => go('/profile')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3029" data-mp="278nyccpe7js3">
                      <UserIcon className="w-4 h-4 text-slate-400" data-id="element-3030" data-mp="2l5kvsx00nzhy" />
                      View Profile
                    </button>
                    <button role="menuitem" onClick={() => go('/business')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3031" data-mp="2q32xq3fm9egg">
                      <BriefcaseIcon className="w-4 h-4 text-slate-400" data-id="element-3032" data-mp="34w6isksf2abx" />
                      Business Hub
                    </button>
                    <button role="menuitem" onClick={() => go('/business')} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-3033" data-mp="24ybk5uw8gy4h">
                      <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-3034" data-mp="2euekqwbfsopb" />
                      Settings
                    </button>
                  </div>

                  <div className="py-1 border-t border-slate-100" data-id="element-3035" data-mp="2okziyin77r7p">
                    <button role="menuitem" onClick={() => {
                setMenuOpen(false);
                logout();
                navigate('/app');
              }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/5 transition-colors" data-id="element-3036" data-mp="2snclyzak9b0s">
                      <LogOutIcon className="w-4 h-4" data-id="element-3037" data-mp="187j590l1dc4a" />
                      Log out
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div> : <button onClick={openLogin} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-medium rounded-full hover:bg-primary-indigo/90 transition-colors shadow-sm" data-id="element-3038" data-mp="vvyppmr74ve8">
          Log in / Sign up
        </button>}
    </header>;
}